import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'
import { Stage, Layer, Line, Circle, Rect, RegularPolygon } from 'react-konva'
import { useNavigate, useLocation } from 'react-router-dom'
import { getAccessToken } from '../Auth/AuthStore.ts'


import { useDrawing } from '../../context/DrawingContext'
import CanvasControls from './CanvasControls'
import styles from './DrawingCanvas.module.css'
import sketchbook from '../../assets/icons/sketchbook.svg'
import TopicSelectionModal, { topics as topicOptions } from './TopicSelectionModal'
import MethodSelectionModal from './MethodSelectionModal'
import ImageUploadCanvas from './ImageUploadCanvas'
import TopicBanner from './TopicBanner'
import AdditionalQuestionsModal from './AdditionalQuestionsModal'
import AnalyzingModal from './AnalyzingModal'
import axios from 'axios'
import { AlertModal } from '../AlertModal'

const DrawingCanvas = () => {
  const { state, dispatch } = useDrawing()
  const [lines, setLines] = useState([])
  const [shapes, setShapes] = useState([])
  const isDrawing = useRef(false)
  const stageRef = useRef()

  const [showTopicModal, setShowTopicModal] = useState(false)
  const [showMethodModal, setShowMethodModal] = useState(false)
  const [selectedTopic, setSelectedTopic] = useState(null)
  const [selectedMethod, setSelectedMethod] = useState(null)  

  const [uploading, setUploading] = useState(false)

  const artboardRef = useRef(null)
  const [size, setSize] = useState({w: 0, h: 0})

  const [imageURL, setImageUrl] = useState(null)
  const [coords, setCoords] = useState(null)

  const [showQuestionsModal, setShowQuestionsModal] = useState(false)
  const [showAnalyzingModal, setShowAnalyzingModal] = useState(false)
  const [answers, setAnswers] = useState([])
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const initialRouteState = useRef(location.state)
  const [healingCourseContext] = useState(initialRouteState.current?.healingCourse || null)
  const [preselectedTopicId] = useState(initialRouteState.current?.preselectedTopicId || null)
  const [isFreeDrawing] = useState(initialRouteState.current?.freeDrawing || false)

  const DEFAULT_COORDS = {
    latitude: 37.505977,
    longitude: 126.956209,
    accuracy: null,   
  }

  useEffect(() => {
    if (lines.length === 0 && shapes.length === 0 && !selectedTopic && !selectedMethod && !preselectedTopicId && !isFreeDrawing) {
      const timer = setTimeout(() => {
        setShowTopicModal(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [lines.length, shapes.length, selectedTopic, selectedMethod, preselectedTopicId, isFreeDrawing])

  useEffect(() => {
    if (initialRouteState.current) {
      initialRouteState.current = null
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    if (preselectedTopicId && !selectedTopic) {
      const topic = topicOptions.find((t) => t.id === preselectedTopicId)
      if (topic) {
        setSelectedTopic(topic)
        setShowTopicModal(false)
        setShowMethodModal(true)
      }
    }
  }, [preselectedTopicId, selectedTopic])

  const handleTopicSelect = useCallback((topic) => {
    setSelectedTopic(topic)
    setShowTopicModal(false)
    setShowMethodModal(true)
  }, [])

  const handleMethodSelect = useCallback((method) => {
    setSelectedMethod(method)
    setShowMethodModal(false)
    // Context에 현재 모드 업데이트
    dispatch({ type: 'SET_CURRENT_MODE', payload: method.id })
  }, [dispatch])
  
  useLayoutEffect(() => {
    if (!artboardRef.current) return
    const el = artboardRef.current
    const update = () => {
      const r = el.getBoundingClientRect()
      setSize({ w: r.width, h: r.height })
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDrawing.current) {
        isDrawing.current = false
        dispatch({ type: 'SET_DRAWING_STATE', payload: false })
        
        dispatch({
          type: 'ADD_TO_HISTORY',
          payload: {
            lines: [...lines],
            shapes: [...shapes],
            timestamp: Date.now()
          }
        })
      }
    }

    document.addEventListener('mouseup', handleGlobalMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [lines, shapes, dispatch])


  const getPointerPosition = useCallback((e) => {
    const stage = e.target.getStage()
    // 터치 이벤트인 경우
    if (e.evt && e.evt.touches && e.evt.touches.length > 0) {
      const touch = e.evt.touches[0]
      const stageBox = stage.container().getBoundingClientRect()
      return {
        x: touch.clientX - stageBox.left,
        y: touch.clientY - stageBox.top
      }
    }
    // 마우스 이벤트인 경우
    return stage.getPointerPosition()
  }, [])

  const handlePointerDown = useCallback((e) => {
    // 터치 이벤트인 경우 기본 동작 방지 (스크롤 등)
    if (e.evt && e.evt.touches) {
      e.evt.preventDefault()
    }

    if (state.tool === 'pen' || state.tool === 'eraser') {
      isDrawing.current = true
      const pos = getPointerPosition(e)
      
      setLines([...lines, {
        id: Date.now(),
        tool: state.tool,
        points: [pos.x, pos.y],
        color: state.tool === 'eraser' ? '#ffffff' : state.color,
        strokeWidth: state.brushSize,
        opacity: state.opacity / 100,
        layerId: state.activeLayerId
      }])
    } else if (['circle', 'rectangle', 'triangle'].includes(state.tool)) {
      const pos = getPointerPosition(e)
      const newShape = {
        id: Date.now(),
        type: state.tool,
        x: pos.x,
        y: pos.y,
        width: 50,
        height: 50,
        fill: state.color,
        opacity: state.opacity / 100,
        layerId: state.activeLayerId
      }
      setShapes([...shapes, newShape])
    }
    
    dispatch({ type: 'SET_DRAWING_STATE', payload: true })
  }, [state, lines, shapes, dispatch, getPointerPosition])

  const handlePointerMove = useCallback((e) => {
    if (!isDrawing.current || !['pen', 'eraser'].includes(state.tool)) {
      return
    }

    // 터치 이벤트인 경우 기본 동작 방지 (스크롤 등)
    if (e.evt && e.evt.touches) {
      e.evt.preventDefault()
    }

    const point = getPointerPosition(e)
    let lastLine = lines[lines.length - 1]

    if (lastLine) {
      lastLine.points = lastLine.points.concat([point.x, point.y])
      lines.splice(lines.length - 1, 1, lastLine)
      setLines(lines.concat())
    }
  }, [state.tool, lines, getPointerPosition])

  const handlePointerUp = useCallback(() => {
    if (isDrawing.current) {
      isDrawing.current = false
      dispatch({ type: 'SET_DRAWING_STATE', payload: false })
      
      dispatch({
        type: 'ADD_TO_HISTORY',
        payload: {
          lines: [...lines],
          shapes: [...shapes],
          timestamp: Date.now()
        }
      })
    }
  }, [lines, shapes, dispatch])

  const handlePointerLeave = useCallback(() => {
    if (isDrawing.current) {
      isDrawing.current = false
      dispatch({ type: 'SET_DRAWING_STATE', payload: false })
      
      // 히스토리에 추가
      dispatch({
        type: 'ADD_TO_HISTORY',
        payload: {
          lines: [...lines],
          shapes: [...shapes],
          timestamp: Date.now()
        }
      })
    }
  }, [lines, shapes, dispatch])

  const clearCanvas = useCallback(() => {
    setLines([])
    setShapes([])
    dispatch({
      type: 'ADD_TO_HISTORY',
      payload: {
        lines: [],
        shapes: [],
        timestamp: Date.now()
      }
    })
  }, [dispatch])

  const saveCanvas = useCallback(() => {
    const uri = stageRef.current.toDataURL()
    const link = document.createElement('a')
    link.download = 'drawing.png'
    link.href = uri
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('브라우저가 geolocation을 지원하지 않아서 기본 좌표 사용')
        resolve(DEFAULT_COORDS)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          })
        },
        (err) => {
          console.warn('위치 정보 획득 실패, 기본 좌표 사용:', err?.message || err)
          resolve(DEFAULT_COORDS) 
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 10000,
        }
      )
    })

  const handleComplete = useCallback(async () => {
    if (!stageRef.current) return
    try {
      setUploading(true)

      /* 사용자 위치 전달 */
      let coords = null;
      try {
        coords = await getPosition();
        setCoords(coords)
      } catch (geoErr) {  
        console.warn('위치 정보 획득 실패:', geoErr?.message || geoErr);
      }

      /* 화질 조절 */      const pixelRatio = 2
      const dataURL = stageRef.current.toDataURL({
        pixelRatio: pixelRatio,
        mimeType: 'image/png',
        quality: 1 
      })
  
      /* Blob으로 변환 */
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      const pngBlob = new Blob([u8arr], { type: mime })
  
      const mimeType = 'image/png'
  
      /* 토큰 발급 */
      const token = getAccessToken();
      if (!token) throw new Error('JWT 발급 실패: token 없음');
  
      /* url 요청 */
      const response = await axios.post(`${import.meta.env.VITE_API_BASE}/api/s3/presigned-url`,
        { mimeType },
        { headers: { Authorization: `Bearer ${token}` } }
      )
  
      const presignedUrl = response.data?.data?.presignedUrl
      if (!presignedUrl) throw new Error('presignedUrl이 응답에 없습니다.')

      /* 업로드 */
      await axios.put(presignedUrl, pngBlob, {
        headers: { 'Content-Type': mimeType },
      })

      /* 실제 URL 추출 */
      const pureImageUrl = presignedUrl.split('?')[0]
      setImageUrl(pureImageUrl)

      /* 후 질문 모달 */
      setShowQuestionsModal(true)
  
    } catch (err) {
      console.error('업로드 실패:', err)
      setShowAlert(true)
      setAlertMessage('업로드 중 오류가 발생했습니다.')
    } finally {
      setUploading(false)
    }

  }, [])

  // topicId를 imageType ENUM으로 변환
  const getImageType = (topicId) => {
    const imageTypeMap = {
      'htp-house': 'ONE',
      'htp-tree': 'TWO',
      'htp-human': 'THREE',
      'pitr': 'FOUR',
    }
    return imageTypeMap[topicId] || 'ONE'
  }

  const handleQuestionsComplete = useCallback(
    async ({ title, followUpQuestions, answers }) => {
      setAnswers(answers)

      setShowQuestionsModal(false)
      setShowAnalyzingModal(true)

      try {
        const token = getAccessToken();
        if (!token) throw new Error('JWT 발급 실패: token 없음')

        const imageType = getImageType(selectedTopic?.id)

        const res = await axios.post(
          `${import.meta.env.VITE_API_BASE}/api/drawing-analysis`,
          {
            imageUrl: imageURL,
            title,
            imageType,
            latitude: coords?.latitude ?? null,
            longitude: coords?.longitude ?? null,
            followUpQuestions,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )

        const analysisResult = res.data?.data

        const drawingData = stageRef.current.toDataURL()

        setShowAnalyzingModal(false)

        navigate('/result', {
          state: {
            drawingData,
            answers,
            topicId: selectedTopic?.id,
            analysisResult,
            healingCourse: healingCourseContext,
          },
        })
      } catch (error) {
        console.error('질문 제출 실패:', error)
        setShowAlert(true)
        setAlertMessage('분석중 오류가 발생했습니다. 다시 시도해주세요.')
        setShowAnalyzingModal(false)
        setShowQuestionsModal(true)
      }
    },
    [navigate, imageURL, coords, selectedTopic, healingCourseContext]
  )

  const undo = useCallback(() => {
    if (state.historyStep >= 0 && state.history.length > 0) {
      if (state.historyStep > 0) {
        const prevState = state.history[state.historyStep - 1]
        dispatch({ type: 'UNDO' })
        setLines(prevState.lines)
        setShapes(prevState.shapes)
      } else if (state.historyStep === 0) {
        dispatch({ type: 'UNDO' })
        setLines([])
        setShapes([])
      }
    }
  }, [state.history, state.historyStep, dispatch])

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' })
    if (state.historyStep < state.history.length - 1) {
      const nextState = state.history[state.historyStep + 1]
      setLines(nextState.lines)
      setShapes(nextState.shapes)
    }
  }, [state.history, state.historyStep, dispatch])

  // 활성 레이어에 따라 필터링
  const visibleLines = lines.filter(line => {
    const layer = state.layers.find(l => l.id === line.layerId)
    return layer?.visible
  })

  const visibleShapes = shapes.filter(shape => {
    const layer = state.layers.find(l => l.id === shape.layerId)
    return layer?.visible
  })

  // 업로드 방식이 선택된 경우 ImageUploadCanvas 표시 (자유 그리기 모드가 아닐 때만)
  if (selectedMethod?.id === 'upload' && !isFreeDrawing) {
    return (
      <>
        <ImageUploadCanvas 
          selectedTopic={selectedTopic}
          onBack={() => {
            setSelectedMethod(null)
            setSelectedTopic(null)
            setShowTopicModal(true)
            dispatch({ type: 'SET_CURRENT_MODE', payload: 'draw' })
          }}
          healingCourseContext={healingCourseContext}
        />
        <TopicSelectionModal
          isOpen={showTopicModal}
          onClose={() => setShowTopicModal(false)}
          onTopicSelect={handleTopicSelect}
        />
        <MethodSelectionModal
          isOpen={showMethodModal}
          onClose={() => {
            setShowMethodModal(false)
            setSelectedTopic(null)
            setShowTopicModal(true)
          }}
          onMethodSelect={handleMethodSelect}
        />
        <AlertModal
          isOpen={showAlert}
          onClose={() => setShowAlert(false)}
          message={alertMessage}
        />
      </>
    )
  }

  return (
    <div className={styles.canvasWrapper}>
      <CanvasControls 
        onClear={clearCanvas}
        onSave={saveCanvas}
        onComplete={handleComplete}
        onUndo={undo}
        onRedo={redo}
        canUndo={state.history.length > 0 && state.historyStep >= 0}
        canRedo={state.historyStep < state.history.length - 1}
        isFreeDrawing={isFreeDrawing}
      />

      <div className={styles.canvasContainer}>
        <div className={styles.artboard} ref={artboardRef}> 
          <img
            src = {sketchbook}
            alt = "스케치북 배경"
            className={styles.canvasBackground} 
          />

          {(selectedTopic || (healingCourseContext && preselectedTopicId)) && (
            <div className={styles.bannerOnPaper}>
              <TopicBanner 
                selectedTopic={selectedTopic || (preselectedTopicId ? topicOptions.find((t) => t.id === preselectedTopicId) : null)}
              />
            </div>
          )}

          <Stage
            ref={stageRef}
            width={size.w}
            height={size.h}
            onMouseDown={handlePointerDown}
            onMousemove={handlePointerMove}
            onMouseup={handlePointerUp}
            onMouseLeave={handlePointerLeave}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            className={styles.stage}
          >
            <Layer
              clipX={0}
              clipY={size.h * 0.0935}              
              clipWidth={size.w}
              clipHeight={size.h * 0.887}
            >
              
              <Rect
                x={0}
                y={0}
                width={size.w}
                height={size.h}
                stroke= "transparent"
                strokeWidth={1}
              />
            
              {visibleLines.map((line) => (
                <Line
                  key={line.id}
                  points={line.points}
                  stroke={line.color}
                  strokeWidth={line.strokeWidth}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                  opacity={line.opacity}
                  globalCompositeOperation={
                    line.tool === 'eraser' ? 'destination-out' : 'source-over'
                  }
                />
              ))}
              {visibleShapes.map((shape) => {
                if (shape.type === 'circle') {
                  return (
                    <Circle
                      key={shape.id}
                      x={shape.x}
                      y={shape.y}
                      radius={shape.width / 2}
                      fill={shape.fill}
                      opacity={shape.opacity}
                    />
                  )
                } else if (shape.type === 'rectangle') {
                  return (
                    <Rect
                      key={shape.id}
                      x={shape.x}
                      y={shape.y}
                      width={shape.width}
                      height={shape.height}
                      fill={shape.fill}
                      opacity={shape.opacity}
                    />
                  )
                } else if (shape.type === 'triangle') {
                  return (
                    <RegularPolygon
                      key={shape.id}
                      x={shape.x}
                      y={shape.y}
                      sides={3}
                      radius={shape.width / 2}
                      fill={shape.fill}
                      opacity={shape.opacity}
                    />
                  )
                }
                return null
              })}
            </Layer>
          </Stage>
        </div>
      </div>

      <TopicSelectionModal
        isOpen={showTopicModal && !isFreeDrawing}
        onClose={() => setShowTopicModal(false)}
        onTopicSelect={handleTopicSelect}
      />

      <MethodSelectionModal
        isOpen={showMethodModal && !isFreeDrawing}
        onClose={() => {
          setShowMethodModal(false)
          setSelectedTopic(null)
          setShowTopicModal(true)
        }}
        onMethodSelect={handleMethodSelect}
      />

      <AdditionalQuestionsModal
        isOpen={showQuestionsModal}
        onClose={() => setShowQuestionsModal(false)}
        onComplete={handleQuestionsComplete}
        topicId={selectedTopic?.id}
        imageURL={imageURL}
        coords={coords}
      />

      <AnalyzingModal
        isOpen={showAnalyzingModal}
        onComplete={() => {}}
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  )
}

export default DrawingCanvas